import React, { useState, useRef } from "react";
import { useDayPlan, PER_PERSON_UNIT } from "../../DayPlanProvider";
import { CustomItemData } from "../../types/dayPlan.types";
import { useToast } from "../../../../contexts/ToastContext";
import { UNIT_OPTIONS } from "../../../../constants/units";
import { getApiErrorMessage } from "../../../../utils/errorUtils";

/** Default VAT rates per category type */
export const DEFAULT_VAT_BY_CATEGORY: Record<string, number> = {
  accommodation: 8,
  gastronomy: 8,
  conference: 23,
  additional: 23,
  "": 23,
};

/** Category options for the dropdown in custom item form */
export const CATEGORY_OPTIONS = [
  { value: "", label: "Niestandardowe" },
  { value: "accommodation", label: "Noclegi" },
  { value: "gastronomy", label: "Gastronomia" },
  { value: "conference", label: "Konferencje" },
  { value: "additional", label: "Dodatki" },
] as const;

export const CustomItemForm: React.FC = () => {
  const { addCustomItem, toggleCustomItemForm, numberOfGuests } =
    useDayPlan();
  const { addToast } = useToast();

  // Track manual quantity edits to prevent auto-fill override
  const userEditedQuantity = useRef<boolean>(false);
  // Track manual VAT edits to prevent category auto-fill override
  const userEditedVat = useRef<boolean>(false);

  const [formData, setFormData] = useState<CustomItemData>({
    customTitle: "",
    timeFrom: "",
    timeTo: "",
    quantity: 1,
    unit: "szt.",
    unitPrice: 0,
    vatRate: 23,
    notes: "",
    customOptionType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.customTitle.trim()) {
      addToast({ type: "warning", message: "Nazwa pozycji jest wymagana" });
      return;
    }

    setIsSubmitting(true);
    try {
      await addCustomItem(formData);

      // Reset form and manual-edit tracking flags
      userEditedQuantity.current = false;
      userEditedVat.current = false;
      setFormData({
        customTitle: "",
        timeFrom: "",
        timeTo: "",
        quantity: 1,
        unit: "szt.",
        unitPrice: 0,
        vatRate: 23,
        notes: "",
        customOptionType: "",
      });
    } catch (error) {
      console.error("Failed to add custom item:", error);
      addToast({
        type: "error",
        message: getApiErrorMessage(error, "Nie udało się dodać pozycji własnej"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    userEditedQuantity.current = false;
    userEditedVat.current = false;
    toggleCustomItemForm();
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-blue-900">
            ✨ NOWA POZYCJA WŁASNA
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Zamknij formularz"
          >
            ✕
          </button>
        </div>

        {/* Category Row */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-6">
            <label htmlFor="custom-item-category" className="block text-sm font-medium text-gray-700 mb-1">
              Kategoria na podsumowaniu:
            </label>
            <select
              id="custom-item-category"
              value={formData.customOptionType || ""}
              onChange={(e) => {
                const newCategory = e.target.value;
                const updates: Partial<CustomItemData> = {
                  customOptionType: newCategory as CustomItemData["customOptionType"],
                };

                // Auto-set VAT only if user has not manually changed it
                if (!userEditedVat.current) {
                  updates.vatRate = DEFAULT_VAT_BY_CATEGORY[newCategory] ?? 23;
                }

                setFormData({ ...formData, ...updates });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* First Row */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-6">
            <label htmlFor="custom-item-title" className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa: *
            </label>
            <input
              id="custom-item-title"
              type="text"
              value={formData.customTitle}
              onChange={(e) =>
                setFormData({ ...formData, customTitle: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nazwa pozycji..."
              required
            />
          </div>

          <div className="col-span-3">
            <label htmlFor="custom-item-time-from" className="block text-sm font-medium text-gray-700 mb-1">
              Od:
            </label>
            <input
              id="custom-item-time-from"
              type="time"
              step="60"
              value={formData.timeFrom}
              onChange={(e) =>
                setFormData({ ...formData, timeFrom: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="col-span-3">
            <label htmlFor="custom-item-time-to" className="block text-sm font-medium text-gray-700 mb-1">
              Do:
            </label>
            <input
              id="custom-item-time-to"
              type="time"
              step="60"
              value={formData.timeTo}
              onChange={(e) =>
                setFormData({ ...formData, timeTo: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-2">
            <label htmlFor="custom-item-quantity" className="block text-sm font-medium text-gray-700 mb-1">
              Ilość:
            </label>
            <input
              id="custom-item-quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => {
                // Mark manual edit to prevent auto-fill override
                userEditedQuantity.current = true;
                setFormData({
                  ...formData,
                  quantity: parseInt(e.target.value) || 1,
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
            />
          </div>

          <div className="col-span-2">
            <label htmlFor="custom-item-unit" className="block text-sm font-medium text-gray-700 mb-1">
              Jednostka:
            </label>
            <select
              id="custom-item-unit"
              value={formData.unit}
              onChange={(e) => {
                const newUnit = e.target.value;
                let newQuantity = formData.quantity;

                // Auto-adjust quantity on unit change unless user manually edited
                if (!userEditedQuantity.current) {
                  if (newUnit === PER_PERSON_UNIT) {
                    newQuantity = numberOfGuests || 1;
                  } else {
                    newQuantity = 1;
                  }
                }

                setFormData({ ...formData, unit: newUnit, quantity: newQuantity });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          <div className="col-span-3">
            <label htmlFor="custom-item-unit-price" className="block text-sm font-medium text-gray-700 mb-1">
              Cena jedn.:
            </label>
            <div className="relative">
              <input
                id="custom-item-unit-price"
                type="number"
                value={formData.unitPrice}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    unitPrice: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
              <span className="absolute right-2 top-2 text-gray-500 text-sm">
                zł
              </span>
            </div>
          </div>

          <div className="col-span-2">
            <label htmlFor="custom-item-vat-rate" className="block text-sm font-medium text-gray-700 mb-1">
              VAT:
            </label>
            <select
              id="custom-item-vat-rate"
              value={formData.vatRate}
              onChange={(e) => {
                userEditedVat.current = true;
                setFormData({ ...formData, vatRate: parseInt(e.target.value) });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>0%</option>
              <option value={5}>5%</option>
              <option value={8}>8%</option>
              <option value={23}>23%</option>
            </select>
          </div>

          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Razem:
            </label>
            <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900 font-medium">
              {((formData.quantity || 1) * (formData.unitPrice || 0)).toFixed(2)}{" "}
              zł
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="custom-item-notes" className="block text-sm font-medium text-gray-700 mb-1">
            Opis:
          </label>
          <input
            id="custom-item-notes"
            type="text"
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Opcjonalny opis..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            ✕ Anuluj
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Dodawanie..." : "✓ Dodaj"}
          </button>
        </div>
      </form>
    </div>
  );
};
